/**
 * Masks an email for display — aligned with backend's maskEmail (auth.helpers.ts).
 *
 * Format: `bu***@ex***.com` (prefix 2 chars + *** + @ + prefix 2 chars domain + *** + TLD).
 *
 * If the input is already masked (contains `***`) or is malformed (no `@`),
 * returns it as-is or a safe fallback — never re-masks.
 */

const FALLBACK_MASK = "***@***.***";

/** Detects if a string is already masked (backend already processed it). */
function isAlreadyMasked(value: string): boolean {
  return value.includes("***");
}

export function maskEmail(email: string): string {
  // Already masked by the backend — display as-is, don't double-mask.
  if (isAlreadyMasked(email)) return email;

  // Malformed input — safe fallback (same as backend).
  const atIndex = email.indexOf("@");
  if (atIndex <= 0 || atIndex === email.length - 1) return FALLBACK_MASK;

  const localPart = email.slice(0, atIndex);
  const domainPart = email.slice(atIndex + 1);
  const lastDot = domainPart.lastIndexOf(".");
  const domainName = lastDot > 0 ? domainPart.slice(0, lastDot) : domainPart;
  const tld = lastDot > 0 ? domainPart.slice(lastDot) : "";

  // Same logic as backend: reveal first 2 chars of local + first 2 chars of domain.
  const localVisible = localPart.slice(0, Math.min(2, localPart.length));
  const domainVisible = domainName.slice(0, Math.min(2, domainName.length));

  return `${localVisible}***@${domainVisible}***${tld}`;
}
